import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { TableState } from '../types';

type RestoreTableState = (
  storedState: Partial<TableState>,
  initialState: TableState,
) => TableState;

type UsePersistentTableStateOptions = {
  restore?: RestoreTableState;
};

const defaultRestoreTableState: RestoreTableState = (
  storedState,
  initialState,
) => {
  return {
    sorting: storedState.sorting ?? initialState.sorting,
    columnFilters: storedState.columnFilters ?? initialState.columnFilters,
    columnVisibility:
      storedState.columnVisibility ?? initialState.columnVisibility,
  };
};

export function usePersistentTableState(
  tableId: string,
  initialState: TableState,
  options: UsePersistentTableStateOptions = {},
): [TableState, Dispatch<SetStateAction<TableState>>] {
  const restore = options.restore ?? defaultRestoreTableState;

  const [tableState, setTableState] = useState<TableState>(() => {
    try {
      const storedState = localStorage.getItem(tableId);
      if (!storedState) {
        return initialState;
      }

      const parsedState = JSON.parse(storedState) as Partial<TableState>;
      return restore(parsedState, initialState);
    } catch (error) {
      localStorage.removeItem(tableId);
      console.error(
        `Couldn't load table ${tableId} state from localStorage: `,
        error,
      );

      return initialState;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(tableId, JSON.stringify(tableState));
  }, [tableId, tableState]);

  return [tableState, setTableState];
}
